import { cn } from "@/lib/utils";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { ErrorBoundary } from "./ErrorBoundary";
import { SiteFooter } from "./SiteFooter";
import { SiteHeader, type HeaderTone } from "./SiteHeader";

export interface PageLayoutProps {
  children: React.ReactNode;
  /**
   * Pull the page up under the header so a full-bleed hero photograph runs
   * behind the nav. Pair with `headerTone="light"`.
   */
  heroBleed?: boolean;
  headerTone?: HeaderTone;
  /** Hide the footer — used by the checkout return screen. */
  hideFooter?: boolean;
  className?: string;
}

/**
 * Header + main + footer. Every route renders inside one of these.
 *
 * The two-prop combination that matters:
 *   heroBleed + headerTone="light"  → landing/parcel pages that open on a photo
 *   (defaults)                      → everything that opens on a cream band
 */
const PageLayout = ({
  children,
  heroBleed = false,
  headerTone,
  hideFooter = false,
  className,
}: PageLayoutProps) => {
  const { pathname } = useLocation();
  const tone: HeaderTone = headerTone ?? (heroBleed ? "light" : "dark");

  return (
    <div className="flex min-h-screen flex-col bg-kf-white">
      {/* A bleeding hero runs *under* the header, so the header must not
          paint over it — see SiteHeader's `transparent`. */}
      <SiteHeader tone={tone} transparent={heroBleed} />
      <main
        id="main"
        className={cn("flex-1", heroBleed && "kf-header-bleed", className)}
      >
        {/* Keyed on the path so a crash on one route clears when you leave it. */}
        <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
      </main>
      {hideFooter ? null : <SiteFooter />}
    </div>
  );
};

export { PageLayout };
export default PageLayout;
